import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin } from "lucide-react";

const CITIES = [
  {
    name: "Napoli",
    tagline: "Radici, passione e vicinanza al territorio",
    img: "https://static.prod-images.emergentagent.com/jobs/0793a609-6c7b-485b-9fe0-b85cb68b4d17/images/f2cbad6882a122f8b9cff9465efc12bbe0b525f1428506985a71b2c3e9346af7.jpeg",
  },
  {
    name: "Imprese e Finanza",
    tagline: "Consulenza per aziende, banche e operazioni finanziarie",
    img: "https://static.prod-images.emergentagent.com/jobs/0793a609-6c7b-485b-9fe0-b85cb68b4d17/images/105858cc07bab63bcffae6adb591b65d11c9820da5bd1ffdc8d6198d35d20b9a.jpeg",
  },
  {
    name: "Milano",
    tagline: "Impresa, diritto societario e contenzioso",
    img: "https://static.prod-images.emergentagent.com/jobs/0793a609-6c7b-485b-9fe0-b85cb68b4d17/images/082fb1f45e88a3111e0e7bbcd5780c375a1f03199fcb604d118b3fcf0550aeb7.jpeg",
  },
  {
    name: "Roma",
    tagline: "Alte Corti e giurisdizioni nazionali",
    img: "https://static.prod-images.emergentagent.com/jobs/0793a609-6c7b-485b-9fe0-b85cb68b4d17/images/c6a7a8a2fa1e63c0baee0d59be710633631ea3b42bae641e0ca5258a4dbcef45.jpeg",
  },
];

export default function CityShowcase() {
  const [active, setActive] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setActive((a) => (a + 1) % CITIES.length), 4800);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const select = (i) => {
    setActive(i);
    startTimer();
  };

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: px * 10, y: py * -8 });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <section id="territorio" className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="city-showcase">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="text-xs uppercase tracking-[0.25em] text-amber-600/80 font-semibold mb-3">Presenza sul Territorio</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium">Napoli · Milano · Roma</h2>
        <p className="text-stone-600 mt-3">Un unico riferimento, tre città al servizio dei clienti.</p>
      </div>

      <div className="relative" style={{ perspective: "1400px" }}>
        <div
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          className="relative w-full aspect-[16/10] md:aspect-[21/9] rounded-3xl overflow-hidden border border-amber-500/25 shadow-[0_40px_80px_-30px_rgba(84,63,25,0.5)]"
          style={{
            transform: `rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
            transformStyle: "preserve-3d",
            transition: "transform 0.25s ease-out",
          }}
          data-testid="city-showcase-stage"
        >
          {CITIES.map((c, i) => (
            <div
              key={c.name}
              className="absolute inset-0"
              style={{ opacity: i === active ? 1 : 0, transition: "opacity 1.1s ease-in-out", zIndex: i === active ? 2 : 1 }}
            >
              <img
                src={c.img}
                alt={`Avv. Fera — ${c.name}`}
                className="w-full h-full object-cover object-top"
                style={{ animation: i === active ? "kenburns 6s ease-out both" : "none" }}
                data-testid={`city-slide-${c.name.toLowerCase()}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c0e]/80 via-transparent to-transparent" />
            </div>
          ))}

          {/* light sweep, replays on each change via key */}
          <div key={active} className="pointer-events-none absolute inset-0 z-[3] overflow-hidden">
            <div
              className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent"
              style={{ animation: "lightSweep 1.4s ease-out both" }}
            />
          </div>

          {/* caption */}
          <div className="absolute left-6 bottom-6 md:left-10 md:bottom-10 z-[4]" style={{ transform: "translateZ(40px)" }}>
            <div key={active} className="animate-fade-up">
              <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.25em] mb-2">
                <MapPin className="w-4 h-4" /> {CITIES[active].name}
              </div>
              <div className="font-serif text-2xl md:text-4xl text-white max-w-md leading-tight drop-shadow-lg">
                {CITIES[active].tagline}
              </div>
            </div>
          </div>
        </div>

        {/* dots */}
        <div className="flex justify-center gap-3 mt-6" data-testid="city-showcase-dots">
          {CITIES.map((c, i) => (
            <button
              key={c.name}
              onClick={() => select(i)}
              data-testid={`city-dot-${c.name.toLowerCase()}`}
              aria-label={`Mostra ${c.name}`}
              className={`h-2 rounded-full transition-all duration-500 ${i === active ? "w-10 bg-amber-500" : "w-2.5 bg-stone-300 hover:bg-amber-400"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
